import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../_core/_services/api.service';
import { NotificationService } from '../../_core/_services/notification.service';
import { CountryChangeRequestDto, ComplianceFlagListItemDto } from '../../_core/_model/country-change';

@Component({
  selector: 'app-country-change-request-detail',
  standalone: false,
  templateUrl: './country-change-requests-detail.html',
  styleUrl: './country-change-requests-detail.css'
})
export class CountryChangeRequestsDetail implements OnInit {
  loading = false;
  acting = false;
  error = '';

  requestId = '';
  request: CountryChangeRequestDto | null = null;
  complianceFlags: ComplianceFlagListItemDto[] = [];

  approveNotes = 'Approved after review';
  rejectNotes = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: ApiService,
    private notify: NotificationService
  ) {}

  ngOnInit(): void {
    this.requestId = this.route.snapshot.paramMap.get('requestId') || '';
    if (!this.requestId) {
      this.error = 'Invalid request id';
      return;
    }

    this.load();
  }

  load() {
    this.loading = true;
    this.error = '';

    this.api.getCountryChangeRequest(this.requestId).subscribe({
      next: (res) => {
        this.request = res.data;
        this.complianceFlags = res.data?.complianceFlags || [];
      },
      error: (err) => {
        this.error = err?.error?.message || 'Failed to load request';
      },
      complete: () => this.loading = false
    });
  }

  approve() {
    if (!this.request || this.request.status !== 'Pending' || this.acting) return;

    this.acting = true;

    this.api.approveCountryChange(this.request.id, {
      notes: this.approveNotes?.trim() || 'Approved after review'
    }).subscribe({
      next: () => {
        this.notify.showSuccess('Country change request approved', 'Success');
        this.load();
      },
      error: (err) => {
        this.notify.showError(err?.error?.message || 'Failed to approve request', 'Error');
      },
      complete: () => this.acting = false
    });
  }

  reject() {
    if (!this.request || this.request.status !== 'Pending' || this.acting) return;

    const notes = this.rejectNotes?.trim();
    if (!notes) {
      this.notify.showWarning('Please enter rejection notes', 'Validation');
      return;
    }

    this.acting = true;

    this.api.rejectCountryChange(this.request.id, { notes }).subscribe({
      next: () => {
        this.notify.showSuccess('Country change request rejected', 'Success');
        this.load();
      },
      error: (err) => {
        this.notify.showError(err?.error?.message || 'Failed to reject request', 'Error');
      },
      complete: () => this.acting = false
    });
  }

  goBack() {
    this.router.navigate(['/admin/country-change-requests']);
  }

  getStatusBadgeClass(status?: string): string {
    switch (status) {
      case 'Approved':
      case 'Applied':
        return 'text-bg-success';
      case 'Rejected':
        return 'text-bg-danger';
      default:
        return 'text-bg-warning';
    }
  }

  getRiskBadgeClass(level?: string | null): string {
    switch (level) {
      case 'Critical':
        return 'text-bg-danger';
      case 'High':
        return 'text-bg-warning';
      case 'Medium':
        return 'text-bg-info';
      case 'Low':
        return 'text-bg-secondary';
      default:
        return 'text-bg-light';
    }
  }

  parseRiskFlags(value?: string | null): string[] {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  getDisplayName(): string {
    return this.request?.fullName || this.request?.email || this.request?.userId || '';
  }

  getDisplayReason(): string {
    return this.request?.requestReason || this.request?.reason || 'No reason provided';
  }

  getDisplayNotes(): string {
    return this.request?.adminReviewNotes || this.request?.notes || 'No notes yet';
  }

  getDisplayCreatedAt(): string | undefined {
    return this.request?.createdAt || this.request?.requestedAt;
  }

  getDisplayReviewedBy(): string {
    return this.request?.reviewedByAdminId || this.request?.reviewedBy || 'N/A';
  }
}